/**
 * SPA theme construction (`createAppTheme`).
 *
 * Per `.kiro/specs/public-website/design.md` §3.2:
 *
 * - The branding tokens (palette, typography, shape, spacing, light + dark
 *   color schemes) flow through `themeOptionsFromBranding(branding)` (lands in
 *   task 4.3) which projects `BrandingConfig` v1 — the workspace contract
 *   shape published as `BrandingTokens` from `@dvgup/contracts/branding-config`
 *   per `models/src/branding-config.ts` — into MUI's `ThemeOptions` shape.
 * - The component-defaults section registers `LinkBehavior` (lands in task
 *   4.2) so every `<Link href="...">` and every link-bearing
 *   `<ButtonBase href="...">` invocation routes through React Router instead
 *   of issuing a full-page navigation. The double-registration on `MuiLink`
 *   (`defaultProps.component`) and `MuiButtonBase` (`defaultProps.LinkComponent`)
 *   is the canonical pattern from the MUI routing-libraries doc, mirrored
 *   verbatim in design §3.2.
 * - Light and dark color schemes are pulled from
 *   `BrandingConfig.theme.{light, dark}` *inside* `themeOptionsFromBranding`
 *   (task 4.3 owns that projection); this file is the thin composition seam
 *   that takes the projected `ThemeOptions` and layers the SPA-routing
 *   component defaults on top before handing the result to MUI's
 *   `createTheme`.
 *
 * The caller wraps the resulting `Theme` in `<ThemeProvider>` (see
 * `App.tsx`, task 4.7).
 */

import type { BrandingTokens } from '@dvgup/contracts/branding-config';
import type { Theme, ThemeOptions } from '@mui/material/styles';
import { createTheme } from '@mui/material/styles';
import { themeOptionsFromBranding } from '../lib/branding/theme-from-branding';
import { LinkBehavior } from './link-behavior';

/**
 * Build the MUI `Theme` for the public-website SPA from a `BrandingConfig`
 * v1 token bundle.
 *
 * @param branding The `BrandingConfig` v1 token bundle imported from
 *   `@dvgup/branding` (or, until that workspace package scaffolds,
 *   from a fixture matching the `BrandingTokens` shape re-exported via
 *   `@dvgup/contracts/branding-config`).
 * @returns A configured MUI `Theme` with `LinkBehavior` registered as the
 *   default link component for both `MuiLink` and `MuiButtonBase`.
 */
export function createAppTheme(branding: BrandingTokens): Theme {
  const baseOptions: ThemeOptions = themeOptionsFromBranding(branding);
  return createTheme({
    ...baseOptions,
    components: {
      ...baseOptions.components,
      MuiLink: {
        ...(baseOptions.components?.MuiLink ?? {}),
        defaultProps: {
          ...(baseOptions.components?.MuiLink?.defaultProps ?? {}),
          component: LinkBehavior,
        },
      },
      MuiButtonBase: {
        ...(baseOptions.components?.MuiButtonBase ?? {}),
        defaultProps: {
          ...(baseOptions.components?.MuiButtonBase?.defaultProps ?? {}),
          LinkComponent: LinkBehavior,
        },
      },
    },
  });
}
